import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Activity, FileText, Weight, ClipboardList } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MealPlanCard } from "./MealPlanCard";
import { TrainingPlanCard } from "./TrainingPlanCard";
import { ExamCard } from "./ExamCard";
import { ReportCard } from "./ReportCard";
import { AnatomopathologyCard } from "./AnatomopathologyCard";

interface GroupedRecordsViewProps {
  records: any[];
  onRecordsChange: (updated: any[]) => void;
  defaultExpanded?: boolean;
}

const recordTypeLabels: Record<string, string> = {
  lab_result: "Exames laboratoriais",
  result: "Exame Laboratorial",
  results: "Exame Laboratorial",
  body_measurement: "Medidas corporais",
  anthropometry: "Antropometria",
  clinical_note: "Notas clínicas",
  vital_signs: "Sinais vitais",
  medication: "Medicações",
  procedure: "Procedimentos",
  diagnosis: "Diagnósticos",
  body_composition: "Composição Corporal",
  physiotherapy_evolution: "Evolução Fisioterapêutica",
  nutritional_assessment: "Avaliação Nutricional",
  meal_plan: "Plano Alimentar",
  training_plan: "Plano de Treino",
  psychotherapy_session: "Sessão de Psicoterapia",
  chiropractic_adjustment: "Ajuste Quiroprático",
  aesthetic_protocol: "Protocolo Estético",
  exam: "Exames de Imagem",
  report: "Laudos Médicos",
  anatomopathology: "Anatomia Patológica",
  patient_info: "Informações do Paciente",
  other: "Outros registros"
};

const recordTypeIcons: Record<string, any> = {
  lab_result: Activity,
  body_measurement: Weight,
  anthropometry: Weight,
  clinical_note: FileText,
  vital_signs: Activity,
  medication: ClipboardList,
  procedure: ClipboardList,
  diagnosis: FileText,
  body_composition: Weight,
  physiotherapy_evolution: Activity,
  nutritional_assessment: ClipboardList,
  meal_plan: ClipboardList,
  training_plan: Activity,
  psychotherapy_session: FileText,
  chiropractic_adjustment: Activity,
  aesthetic_protocol: ClipboardList,
  exam: Activity,
  report: FileText,
  anatomopathology: Activity,
  patient_info: FileText,
  other: FileText
};

export function GroupedRecordsView({ records, onRecordsChange, defaultExpanded = false }: GroupedRecordsViewProps) {
  const groupedRecords = records.reduce((acc, record) => {
    const type = record.type || "other";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(record);
    return acc;
  }, {} as Record<string, any[]>);

  // Separar exames, laudos, anatomopatologia e planos especiais para renderização especial
  // Exames laboratoriais são renderizados apenas em GroupedResultsView
  const examRecords = groupedRecords['exam'] || [];
  const reportRecords = groupedRecords['report'] || [];
  const anatomopathologyRecords = groupedRecords['anatomopathology'] || [];
  const trainingRecords = groupedRecords['training_plan'] || [];
  const otherRecords = Object.entries(groupedRecords).filter(
    ([type]) => type !== 'exam' && type !== 'report' && type !== 'anatomopathology' && type !== 'lab_result' && type !== 'training_plan'
  );

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string | null | undefined, recordType?: string): string => {
    if (recordType === 'clinical_note' && !dateStr) {
      return format(new Date(), "dd/MMM/yyyy", { locale: ptBR });
    }

    if (!dateStr) {
      return format(new Date(), "dd/MMM/yyyy", { locale: ptBR });
    }

    try {
      return format(new Date(dateStr), "dd/MMM/yyyy", { locale: ptBR });
    } catch {
      return format(new Date(), "dd/MMM/yyyy", { locale: ptBR });
    }
  };

  const getRecordContent = (record: any): string | JSX.Element => {
    const type = record.type || "other";

    switch (type) {
      case "report":
        if (record.text) return record.text;
        if (record.report_type) {
          const specialty = record.specialty ? ` (${record.specialty})` : '';
          return `${record.report_type}${specialty}`;
        }
        if (record.title) return record.title;
        return "Laudo Médico";

      case "exam":
        if (record.exam_type) return record.exam_type;
        if (record.title) return record.title;
        return "Exame de Imagem";

      case "lab_result":
        if (record.test_name && record.value !== undefined) {
          const refRange = record.ref_low && record.ref_high
            ? ` (Ref: ${record.ref_low}-${record.ref_high})`
            : '';
          return `${record.test_name}: ${record.value}${record.unit ? ` ${record.unit}` : ''}${refRange}`;
        }
        return "Resultado laboratorial";

      case "body_measurement":
        if (record.parameter && record.value !== undefined) {
          return `${record.parameter}: ${record.value}${record.unit ? ` ${record.unit}` : ''}`;
        }
        return "Medida corporal";

      case "clinical_note":
        if (record.category) return record.category;
        if (record.title) return record.title;
        return "Nota clínica";

      case "vital_signs":
        if (record.sign_type && record.value !== undefined) {
          return `${record.sign_type}: ${record.value}${record.unit ? ` ${record.unit}` : ''}`;
        }
        return "Sinais vitais";

      case "physiotherapy_evolution":
        if (record.session_number) {
          return `Sessão #${record.session_number}`;
        }
        return "Evolução Fisioterapêutica";

      case "training_plan":
        if (record.exercise) {
          return record.exercise;
        }
        break;
    }

    return record.extracted_content || "Sem conteúdo extraído";
  };

  const totalRecords = records.length;

  if (totalRecords === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">Nenhum registro estruturado encontrado.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {Object.keys(groupedRecords).length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">Nenhum registro estruturado encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Exames de Imagem - Layout Visual Melhorado */}
          {examRecords.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Exames de Imagem ({examRecords.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {examRecords.map((exam, index) => (
                  <ExamCard
                    key={`exam-${index}`}
                    record={exam}
                    onUpdate={(updated) => {
                      const newRecords = [...records];
                      const recordIndex = records.findIndex(r => r === exam);
                      newRecords[recordIndex] = updated;
                      onRecordsChange(newRecords);
                    }}
                    onDelete={() => {
                      onRecordsChange(records.filter(r => r !== exam));
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Laudos Médicos - Layout Visual Melhorado */}
          {reportRecords.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Laudos Médicos ({reportRecords.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reportRecords.map((report, index) => (
                  <ReportCard
                    key={`report-${index}`}
                    record={report}
                    onUpdate={(updated) => {
                      const newRecords = [...records];
                      const recordIndex = records.findIndex(r => r === report);
                      newRecords[recordIndex] = updated;
                      onRecordsChange(newRecords);
                    }}
                    onDelete={() => {
                      onRecordsChange(records.filter(r => r !== report));
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Anatomia Patológica - Layout Visual Especializado */}
          {anatomopathologyRecords.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Anatomia Patológica ({anatomopathologyRecords.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {anatomopathologyRecords.map((anapath, index) => (
                  <AnatomopathologyCard
                    key={`anatomopathology-${index}`}
                    record={anapath}
                    onUpdate={(updated) => {
                      const newRecords = [...records];
                      const recordIndex = records.findIndex(r => r === anapath);
                      newRecords[recordIndex] = updated;
                      onRecordsChange(newRecords);
                    }}
                    onDelete={() => {
                      onRecordsChange(records.filter(r => r !== anapath));
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Outros Tipos de Registros - Mantém Layout Técnico Atual */}
          {otherRecords.map(([type, typeRecords]) => {
            const recordsArray = typeRecords as any[];
            const isExpanded = expandedGroups.has(type);
            const label = recordTypeLabels[type] || type;
            const Icon = recordTypeIcons[type] || FileText;

            return (
              <Collapsible key={type} open={isExpanded} onOpenChange={() => toggleGroup(type)}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          )}
                          <Icon className="h-5 w-5 text-primary" />
                          <CardTitle className="text-base font-medium">{label}</CardTitle>
                        </div>
                        <Badge variant="outline">{recordsArray.length}</Badge>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="space-y-3">
                      {recordsArray.map((record, idx) => (
                        <div
                          key={idx}
                          className="p-4 border rounded-lg bg-card hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              {record.type === 'clinical_note' ? (
                                <div className="space-y-2">
                                  <p className="font-medium text-sm mb-1">{getRecordContent(record)}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(record.event_date || record.date, record.type)}
                                  </p>
                                  {record.description && (
                                    <div className="mt-3 p-3 bg-muted/50 rounded-md">
                                      <p className="text-sm">{record.description}</p>
                                    </div>
                                  )}
                                </div>
                              ) : record.type === 'physiotherapy_evolution' ? (
                                <div className="space-y-2">
                                  <p className="font-medium text-sm mb-1">{getRecordContent(record)}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(record.event_date || record.date, record.type)}
                                  </p>
                                  {record.patient_status && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Status:</p>
                                      <p className="text-sm">{record.patient_status}</p>
                                    </div>
                                  )}
                                  {record.pain_level !== undefined && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Nível de Dor:</p>
                                      <p className="text-sm">{record.pain_level}/10</p>
                                    </div>
                                  )}
                                  {record.interventions && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Intervenções:</p>
                                      <p className="text-sm">{record.interventions}</p>
                                    </div>
                                  )}
                                  {record.exercises && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Exercícios:</p>
                                      <p className="text-sm">{record.exercises}</p>
                                    </div>
                                  )}
                                  {record.observations && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Observações:</p>
                                      <p className="text-sm">{record.observations}</p>
                                    </div>
                                  )}
                                  {record.next_goals && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-muted-foreground">Próximas Metas:</p>
                                      <p className="text-sm">{record.next_goals}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <p className="font-medium text-sm mb-1">{getRecordContent(record)}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(record.event_date || record.date, record.type)}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <details className="text-xs text-muted-foreground">
                            <summary className="cursor-pointer hover:text-foreground">Ver JSON completo</summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                              {JSON.stringify(record, null, 2)}
                            </pre>
                          </details>
                        </div>
                      ))}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </>
      )}

      <MealPlanCard records={records} onRecordsChange={onRecordsChange} />
      <TrainingPlanCard records={records} onRecordsChange={onRecordsChange} />
    </div>
  );
}
